import { authHeaders, guestApi, isGuestHunt, resolveUrl } from '../api';

/**
 * Q i jakten (QJ1–QJ3, MT1) – klienten mot /api/q/hunt (API q/huntRoutes.ts).
 *
 * - Det telefonen hör (transkriberat tal och skott) sparas på servern och visas i Q-fliken hos alla.
 * - "Lasse till Ola …" skickar det som sägs som röstmeddelande till Ola (walkie-talkie via Q).
 * - Radion (LiveKit) startas först när någon trycker på walkie-talkien eller kameran (RQ1), och stängs för
 *   alla när ingen pratat i den på 15 minuter (RQ2, servern avgör i flödet).
 * - "Q, …" i det telefonen hör är en fråga till Q (RQ4, väckningsord).
 */

export type HeardLine = { id: string; speaker: string; text: string; ts: number; lat: number | null; lng: number | null; isMe: boolean };
export type VoiceMessage = { id: string; from: string; to: string; audioUrl: string | null; text: string | null; ts: number; heard: boolean; toMe: boolean };
export type HuntQFeed = { heard: HeardLine[]; voice: VoiceMessage[]; radioStartedAt: number | null; summary: string | null };
export type HuntSummary = { summary: string; transcript: string; mode: 'live' | 'mock'; cached?: boolean };

const base = (meetingId: string) => resolveUrl(`/api/q/hunt/${encodeURIComponent(meetingId)}`);

async function json<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body as { error?: string }).error || `HTTP ${res.status}`);
  return body as T;
}

export const huntQApi = {
  feed: (meetingId: string, after?: number) =>
    fetch(`${base(meetingId)}/feed${after ? `?after=${Math.floor(after)}` : ''}`, { headers: authHeaders() }).then(r => json<HuntQFeed>(r)),
  heard: (meetingId: string, line: { clientId: string; text: string; lat?: number; lng?: number; ts?: number }) =>
    fetch(`${base(meetingId)}/heard`, { method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(line) }).then(r => json<HeardLine>(r)),
  /** Röstmeddelande till en deltagare (förnamn). Ljud om det finns, annars bara text (läses upp hos mottagaren). */
  voice: (meetingId: string, to: string, opts: { audio?: Blob | null; text?: string | null }) => {
    const fd = new FormData();
    fd.append('to', to);
    if (opts.text) fd.append('text', opts.text);
    if (opts.audio) fd.append('audio', opts.audio, opts.audio.type.includes('mp4') ? 'walkie.m4a' : 'walkie.webm');
    return fetch(`${base(meetingId)}/voice`, { method: 'POST', headers: authHeaders(), body: fd }).then(r => json<{ id: string; to: string }>(r));
  },
  voiceHeard: (meetingId: string, id: string) =>
    fetch(`${base(meetingId)}/voice/${encodeURIComponent(id)}/heard`, { method: 'POST', headers: authHeaders() }).then(r => json<{ ok: boolean }>(r)),
  startRadio: (meetingId: string) =>
    fetch(`${base(meetingId)}/radio`, { method: 'POST', headers: authHeaders() }).then(r => json<{ radioStartedAt: number }>(r)),
  /** RQ2: någon pratar (eller sänder video) i radion; håller den igång. En gäst (GU1) går via gäst-API:t med gästtokenen. */
  radioActivity: (meetingId: string): Promise<{ radioStartedAt: number | null }> =>
    isGuestHunt(meetingId)
      ? guestApi.radioActivity()
      : fetch(`${base(meetingId)}/radio/activity`, { method: 'POST', headers: authHeaders() }).then(r => json<{ radioStartedAt: number | null }>(r)),
  summary: (meetingId: string) =>
    fetch(`${base(meetingId)}/summary`, { method: 'POST', headers: authHeaders() }).then(r => json<HuntSummary>(r)),
};

/** Ord som inte är anropsnamn ("ner till bilen"). Samma lista som API:ts q/huntQ.ts. */
const NOT_CALLSIGN = new Set(['ner', 'upp', 'fram', 'hem', 'bort', 'in', 'ut', 'över', 'tillbaka', 'jag', 'vi', 'du', 'ni', 'han', 'hon', 'den', 'det', 'och', 'går', 'gå', 'kom', 'kör', 'åk', 'sitt', 'runt', 'mot', 'ända', 'vägen', 'passet', 'bilen', 'tornet']);

/** "Lasse till Ola, rådjur på väg mot dig" → { from, to, rest }; bara först i en rad. */
export function parseWalkie(text: string): { from: string; to: string; rest: string } | null {
  const m = text.trim().match(/^([\p{L}][\p{L}-]{1,24})\s+till\s+([\p{L}][\p{L}-]{1,24})(?![\p{L}])[\s,.:;!—–-]*(.*)$/u);
  if (!m) return null;
  const [, from, to, rest] = m;
  if (NOT_CALLSIGN.has(from.toLowerCase()) || NOT_CALLSIGN.has(to.toLowerCase())) return null;
  return { from, to, rest: rest.trim().replace(/^(kom|över)[\s,.]*/i, '').trim() };
}

/** Efter ett anrop utan innehåll ("Lasse till Ola" – paus) går nästa bit med tal till samma mottagare inom den här tiden. */
export const WALKIE_FOLLOW_MS = 20_000;

/**
 * Walkie-läget för den som pratar: avgör vart en nyss transkriberad bit ska.
 * - anrop med innehåll → skicka biten till mottagaren
 * - anrop utan innehåll → vänta på nästa bit (inom WALKIE_FOLLOW_MS)
 * - vanlig rad direkt efter ett tomt anrop → skicka den till den väntande mottagaren
 */
export function createWalkieRouter(now: () => number = Date.now) {
  let pending: { to: string; at: number } | null = null;
  return (text: string): { to: string; text: string } | null => {
    const w = parseWalkie(text);
    if (w) {
      if (!w.rest) { pending = { to: w.to, at: now() }; return null; }
      pending = null;
      return { to: w.to, text: w.rest };
    }
    if (pending && now() - pending.at <= WALKIE_FOLLOW_MS) {
      const to = pending.to;
      pending = null;
      return { to, text };
    }
    pending = null;
    return null;
  };
}

/** Nya röstmeddelanden till mig som ska spelas upp (inte hörda, inte redan spelade i den här sessionen). */
export function voiceToPlay(voice: VoiceMessage[], played: Set<string>): VoiceMessage[] {
  return voice.filter(v => v.toMe && !v.heard && !played.has(v.id));
}

/** RQ2: hur ofta en ansluten telefon säger till servern att någon pratat (eller sänt video) i radion. */
export const RADIO_ACTIVITY_MS = 60_000;

/** RQ2: servern har stängt radion (den var startad och är det inte längre). */
export function radioClosedByServer(prev: number | null, next: number | null): boolean {
  return prev != null && next == null;
}

/** EJ5: visas när jakten startar – Q lyssnar från start (RQ) och "Q, …" är en fråga till Q (RQ4). */
export const HUNT_START_Q_TEXT = 'Q lyssnar och antecknar vad som händer, säg Q följt av din fråga om du undrar något';

/** Hur länge en notis under jakten visas; starttexten är längre och får tid att läsas. */
export function huntNoticeMs(text: string): number {
  return text === HUNT_START_Q_TEXT ? 9000 : 4500;
}

// Väckningsordet som taligenkänningen skriver det: "Q", "Kju", "Ku" (svenska), "Cue"/"Queue" (engelska).
const WAKE_RE = /^(?:(?:hej|hallå|okej|ok|du)[\s,]+)?(?:q|kju|kiu|kyu|ku|kew|cue|queue)(?![\p{L}\d])[\s,.:;!?—–-]*(.*)$/iu;

/**
 * RQ4: "Q, hur långt är det till tornet?" → "hur långt är det till tornet?". Bara "Q" → "" (Q lyssnar efter
 * frågan). Allt annat → null. "Ku till Ola" är ett walkie-anrop, inte en fråga till Q.
 */
export function parseWakeWord(text: string): string | null {
  const m = text.trim().match(WAKE_RE);
  if (!m) return null;
  const rest = m[1].trim();
  if (/^till(?![\p{L}])/iu.test(rest)) return null;
  return rest;
}
